import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, MapPin } from 'lucide-react';
import { api, FALLBACK_IMAGE as FALLBACK } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal } from '../components/Reveal';
import { ProductTile, TileSkeleton } from '../components/ProductTile';
import { Brush } from '../components/Brush';
import { Img } from '../components/Img';
import { collectionCover } from '../lib/collectionCovers';

export default function CollectionDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/collections/${slug}?lang=${lang}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="wrap py-32 text-center">
        <h1 className="text-4xl">404</h1>
        <Link to="/collezioni" className="btn btn-line mt-8">
          {t('common.allCollections')}
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wrap py-32">
        <p className="text-[14px] text-inchiostro/70">{t('common.loading')}</p>
        <div className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <TileSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  const story = data.story;
  // scatto d'ambiente della collezione (ASSETS A-10), null se non c'e'
  const cover = collectionCover(slug);

  return (
    <>
      <Seo
        title={story?.seo_title || data.name}
        suffix={story?.seo_title ? '' : undefined}
        description={
          story?.seo_meta ||
          story?.story?.slice(0, 155) ||
          `${data.name}: ${data.product_count} referenze VdE Tivoli 1996.`
        }
        image={collectionCover(slug, '-54') || data.images?.[0]}
        path={`/collezioni/${slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${data.name} · VdE Tivoli 1996`,
          description: story?.story || '',
          numberOfItems: data.product_count,
        }}
      />

      <nav aria-label="breadcrumb" className="wrap pt-6">
        <Link
          to="/collezioni"
          data-testid="back-to-collections"
          className="inline-flex min-h-[44px] items-center gap-2 text-[14px] font-bold text-inchiostro/70 transition-colors hover:text-marsala"
        >
          <ChevronLeft size={13} /> {t('nav.collections')}
        </Link>
      </nav>

      {slug === 'victionary' && (
        <div className="wrap pt-6">
          <Link
            to="/storie/victionary"
            data-testid="victionary-experience-banner"
            className="inline-flex min-h-[44px] items-center gap-2 border border-marsala/40 bg-marsala/5 px-4 py-2.5 text-sm font-semibold text-marsala transition-colors hover:bg-marsala hover:text-carta"
          >
            {t('collections.victionaryBanner')}
            <ArrowUpRight size={15} strokeWidth={2} />
          </Link>
        </div>
      )}

      <header className="relative pb-14 pt-8 md:pb-20 md:pt-10">
        <div className="wrap grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <div>
            <h1 className="relative">
              <span className="display-xl block">{data.name}</span>
              <Brush className="absolute -bottom-2 left-0 h-[0.36em] w-[68%]" opacity={0.36} />
            </h1>
            {story?.claim && (
              <p className="mt-7 font-display text-2xl italic text-marsala md:text-3xl">
                {story.claim}
              </p>
            )}
            <p className="prosa mt-7 border-t-2 border-inchiostro pt-6 text-base md:text-lg">
              {story?.story || t('collections.fallbackStory')}
            </p>

            {story?.use && (
              <div className="mt-8 border-l-2 border-marsala/40 pl-5">
                <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-marsala">
                  {t('collections.howToUse')}
                </p>
                <p className="prosa mt-2 text-sm text-inchiostro/85">{story.use}</p>
              </div>
            )}

            {/* Il conteggio delle referenze e l'elenco delle categorie occupavano
                mezza schermata per dire due cose che si vedono da sole: i pezzi
                stanno nella griglia qui sotto, la categoria e' nella scheda di
                ognuno. Della Shade invece resta traccia: e' l'unico posto in cui
                una collezione dichiara il suo colore. */}
            {data.shades?.length > 0 && (
              <p className="mt-8 text-sm text-inchiostro/70">
                Shade <span className="text-inchiostro">{data.shades.join(' · ')}</span>
              </p>
            )}

            <Link to="/dove-comprare" data-testid="collection-cta-where" className="btn btn-solid mt-9">
              <MapPin size={14} strokeWidth={2.2} />
              {t('common.findRetailer')}
            </Link>
          </div>

          {cover ? (
            /* Scatto d'ambiente 3:4: la collezione si presenta in tavola. I
               packshot sono gia' tutti nella griglia qui sotto. Niente onError:
               e' un asset nostro (vedi Img). */
            <div
              data-testid="collection-cover"
              className="relative aspect-[3/4] w-full max-w-[560px] overflow-hidden bg-carta-2 lg:max-w-none"
            >
              <Img
                src={cover}
                alt={data.name}
                width={960}
                aspectRatio={3 / 4}
                priority
                sizes="(min-width:1024px) 45vw, (min-width:640px) 560px, 100vw"
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div data-testid="collection-packshots" className="grid grid-cols-2 gap-3 md:gap-5">
              {(data.images || []).slice(0, 4).map((src, i) => (
                <div
                  key={src}
                  className={`overflow-hidden bg-carta-2 ${i % 3 === 0 ? 'aspect-square' : 'aspect-[4/5]'} ${
                    i === 1 ? 'mt-8 md:mt-14' : ''
                  }`}
                >
                  <Img
                    src={src}
                    alt={`${data.name} ${i + 1}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    sizes="50vw"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK;
                    }}
                    className="h-full w-full object-contain p-5 mix-blend-multiply"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      <section data-testid="collection-products" className="py-14 md:py-20">
        <div className="wrap">
          <h2 className="accento mb-9 text-2xl text-inchiostro/85">{t('collections.inCollection')}</h2>
          <div className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 md:gap-x-7 lg:grid-cols-4">
            {data.products.map((p, i) => (
              <Reveal key={p.sku} delay={(i % 4) * 0.05}>
                <ProductTile product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {data.related?.length > 0 && (
        <section className="bg-carta-2 py-14 md:py-20">
          <div className="wrap">
            <h2 className="accento mb-9 text-2xl text-inchiostro/85">{t('common.pairWith')}</h2>
            <div className="grid grid-cols-2 gap-5 md:grid-cols-4 md:gap-7">
              {data.related.map((c, i) => (
                <Reveal key={c.slug} delay={i * 0.05}>
                  <Link
                    to={`/collezioni/${c.slug}`}
                    data-testid={`related-${c.slug}`}
                    className="group block"
                  >
                    <div className="aspect-[5/4] overflow-hidden bg-carta">
                      {collectionCover(c.slug) ? (
                        <Img
                          src={collectionCover(c.slug, '-54')}
                          alt={c.name}
                          aspectRatio={5 / 4}
                          sizes="(min-width:768px) 25vw, 50vw"
                          className="img-zoom h-full w-full object-cover"
                        />
                      ) : (
                        <Img
                          src={c.images?.[0] || FALLBACK}
                          alt={c.name}
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK;
                          }}
                          className="img-zoom h-full w-full object-contain p-6 mix-blend-multiply"
                        />
                      )}
                    </div>
                    <h3 className="mt-4 flex items-center gap-2 font-display text-xl transition-colors group-hover:text-marsala">
                      {c.name}
                      <ArrowUpRight
                        size={15}
                        strokeWidth={1.6}
                        className="text-marsala transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
